// Client logs are shipped to Loki as-is, so credentials picked up by the
// avcore/mediasoup loggers (JWTs, ICE/TURN credentials, DTLS fingerprints, SDPs)
// and PII (screen-share track labels) are scrubbed here before serialization.
// Everything else (ids, kinds, codecs, timestamps, error messages) is kept.

export const REDACTED = "[REDACTED]";

const MAX_DEPTH = 20;
// Total values visited per log entry, bounds the work on huge payloads.
const MAX_NODES = 10_000;
// Stringified JSON longer than this is only pattern-scrubbed, not parsed.
const MAX_JSON_PARSE_LENGTH = 256 * 1024;

// Compared lowercased, without "_" and "-".
const SECRET_KEYS = new Set([
  "token",
  "accesstoken",
  "authtoken",
  "refreshtoken",
  "idtoken",
  "authorization",
  "password",
  "credential",
  "credentials",
  "usernamefragment",
  "secret",
  "clientsecret",
  "apikey"
]);

// Secrets only in a given parent: TURN usernames, DTLS fingerprint values.
const SECRET_KEYS_BY_PARENT: Record<string, Set<string>> = {
  iceservers: new Set(["username"]),
  fingerprints: new Set(["value"])
};

// JWTs (avcore tokens, account JWTs) are redacted by redactJwts() before
// these patterns run. Every pattern here must stay linear on any input: strings
// are scrubbed whatever their length.
const STRING_PATTERNS: Array<[RegExp, string]> = [
  // Authorization header values.
  [/\b(Bearer|Basic)\s+[\w.~+/=-]+/gi, `$1 ${REDACTED}`],
  // Query string / form credentials.
  [
    /\b((?:access_|auth_|refresh_|id_)?token|api_?key|password|secret)=[^&#\s"'<>,;\\]+/gi,
    `$1=${REDACTED}`
  ],
  // Same, URL-encoded inside another parameter (e.g. redirect=%2Fcb%3Ftoken%3D...).
  // An encoded separator ("%3F", "%26") glues the name to a word character,
  // so \b alone misses it.
  [
    /(%[0-9A-Fa-f]{2}|\b)((?:access_|auth_|refresh_|id_)?token|api_?key|password|secret)%3D(?:[^%&#\s"'<>,;\\]|%(?!26)[0-9A-Fa-f]{2})+/gi,
    `$1$2%3D${REDACTED}`
  ],
  // SDP ICE credentials and SRTP keys.
  [/(a=(?:ice-pwd|ice-ufrag|crypto):)[^\r\n"\\]*/g, `$1${REDACTED}`],
  // SDP DTLS fingerprint, keeps the hash algorithm.
  [/(a=fingerprint:[\w-]+ ?)[^\r\n"\\]*/g, `$1${REDACTED}`],
  // Bare DTLS fingerprints (sha-1 and longer, colon separated hex).
  [/\b(?:[0-9A-Fa-f]{2}:){15,}[0-9A-Fa-f]{2}\b/g, REDACTED],
  // Secret keys inside JSON that could not be parsed (e.g. truncated).
  [
    /("(?:(?:access_?|auth_?|refresh_?|id_?)?token|authorization|password|credentials?|usernameFragment|secret|api_?key)"\s*:\s*)"(?:[^"\\]|\\.)*"/gi,
    `$1"${REDACTED}"`
  ]
];

/**
 * Returns a redacted deep copy of a log payload, ready for JSON.stringify.
 * Never mutates the input and never throws.
 */
export function redactLog(value: unknown): unknown {
  try {
    return new LogRedactor().redact(value, 0, undefined);
  } catch {
    return "[UNREDACTABLE LOG]";
  }
}

/** Redacts then serializes a log payload; strings are scrubbed as text. */
export function serializeLog(value: unknown): string {
  try {
    const redacted = redactLog(value);
    return typeof redacted === "string"
      ? redacted
      : JSON.stringify(redacted) ?? String(redacted);
  } catch {
    return "[UNSERIALIZABLE LOG]";
  }
}

function summarizeSdp(sdp: string): string {
  const media = Array.from(sdp.matchAll(/^m=(\w+)/gm), (_) => _[1]);
  const codecs = (sdp.match(/^a=rtpmap:/gm) ?? []).length;
  return `[REDACTED SDP length=${sdp.length} media=${media.join(
    ","
  )} codecs=${codecs}]`;
}

class LogRedactor {
  private nodes = 0;
  private readonly ancestors = new WeakSet<object>();

  private get exhausted() {
    return this.nodes >= MAX_NODES;
  }

  redact(
    value: unknown,
    depth: number,
    parentKey: string | undefined
  ): unknown {
    if (this.exhausted) {
      return "[TRUNCATED]";
    }
    this.nodes++;

    if (typeof value === "string") {
      return this.redactString(value, depth);
    }
    if (typeof value === "bigint") {
      // JSON.stringify throws on bigint.
      return value.toString();
    }
    if (typeof value !== "object" || value === null) {
      return value;
    }
    if (depth >= MAX_DEPTH) {
      return "[MAX DEPTH]";
    }
    if (this.ancestors.has(value)) {
      return "[CIRCULAR]";
    }

    this.ancestors.add(value);
    try {
      return this.redactObject(value, depth, parentKey);
    } catch {
      return "[UNSERIALIZABLE]";
    } finally {
      this.ancestors.delete(value);
    }
  }

  private redactString(value: string, depth: number): string {
    if (isSdp(value)) {
      return summarizeSdp(value);
    }

    const json = this.redactJsonString(value, depth);
    if (json !== undefined) {
      return json;
    }

    return STRING_PATTERNS.reduce(
      (_, [pattern, replacement]) => _.replace(pattern, replacement),
      redactJwts(value)
    );
  }

  // Loggers often pre-stringify payloads (e.g. avcore's socket messages).
  private redactJsonString(value: string, depth: number) {
    const trimmed = value.trim();
    if (
      trimmed.length > MAX_JSON_PARSE_LENGTH ||
      !/^[[{]/.test(trimmed) ||
      !/[\]}]$/.test(trimmed)
    ) {
      return undefined;
    }

    let parsed: unknown;
    try {
      parsed = JSON.parse(trimmed);
    } catch {
      return undefined;
    }

    return JSON.stringify(this.redact(parsed, depth + 1, undefined));
  }

  private redactObject(value: object, depth: number, parentKey?: string) {
    if (value instanceof Error) {
      // JSON.stringify drops the non-enumerable name/message/stack.
      return this.redactEntries(
        {
          ...value,
          name: value.name,
          message: value.message,
          stack: value.stack
        },
        depth,
        parentKey
      );
    }

    const toJSON = (value as { toJSON?: unknown }).toJSON;
    if (typeof toJSON === "function") {
      return this.redact(toJSON.call(value), depth + 1, parentKey);
    }

    if (Array.isArray(value)) {
      const result: unknown[] = [];
      for (const item of value) {
        if (this.exhausted) {
          result.push(`[TRUNCATED ${value.length - result.length} items]`);
          break;
        }
        // Array items inherit the array's key (e.g. iceServers[i].username).
        result.push(this.redact(item, depth + 1, parentKey));
      }
      return result;
    }

    return this.redactEntries(value, depth, parentKey);
  }

  private redactEntries(value: object, depth: number, parentKey?: string) {
    const parentSecrets = parentKey
      ? SECRET_KEYS_BY_PARENT[parentKey]
      : undefined;
    // Shape of a serialized MediaStreamTrack: labels carry window/screen titles.
    const isTrack = "readyState" in value && "kind" in value;
    const result: Record<string, unknown> = {};

    for (const key of Object.keys(value)) {
      if (this.exhausted) {
        result["[TRUNCATED]"] = true;
        break;
      }

      const item = (value as Record<string, unknown>)[key];
      const normalizedKey = normalizeKey(key);

      if (
        SECRET_KEYS.has(normalizedKey) ||
        parentSecrets?.has(normalizedKey) ||
        (isTrack && normalizedKey === "label")
      ) {
        result[key] = redactSecret(item);
      } else if (normalizedKey === "sdp" && typeof item === "string") {
        result[key] = summarizeSdp(item);
      } else {
        result[key] = this.redact(item, depth + 1, normalizedKey);
      }
    }

    return result;
  }
}

// JWT: base64url header ("eyJ" is '{"'), payload and signature, dot separated.
// A single regex (/\beyJ[\w-]{2,}\.[\w-]{2,}\.[\w-]*/g) is quadratic: on a
// long [\w-] run with many "eyJ" it restarts at each one and rescans to the
// end of the run (16 s for 200 KB). Instead, maximal runs of token characters
// are matched (each character is consumed once) and each run is split on "."
// and scanned segment by segment, so the whole pass is linear.
const JWT_HEADER = "eyJ";
const TOKEN_RUN = /[\w.-]+/g;

function redactJwts(value: string): string {
  return value.includes(JWT_HEADER)
    ? value.replace(TOKEN_RUN, redactJwtsInRun)
    : value;
}

// Every segment i that can start eyJ[\w-]{2,}\.[\w-]{2,}\.[\w-]* (a header
// with "eyJ" and 2+ chars after it, a 2+ char payload, then a signature) opens
// a window over segments i..i+2. The union of all windows is redacted, from the
// first header start of the block on. Windows are merged, not taken greedily
// left to right, so a word containing "eyJ" just before a JWT (keyJar.<jwt>)
// cannot leave the JWT payload in clear. Each segment is examined once.
function redactJwtsInRun(run: string): string {
  if (!run.includes(JWT_HEADER)) {
    return run;
  }
  const segments = run.split(".");
  const result: string[] = [];
  // Index of the last segment of the current redacted block.
  let blockEnd = -1;
  for (let i = 0; i < segments.length; i++) {
    const start = jwtHeaderStart(segments, i);
    if (i <= blockEnd) {
      // Inside a block: an overlapping window extends it.
      if (start !== -1) {
        blockEnd = i + 2;
      }
    } else if (start !== -1) {
      result.push((segments[i] ?? "").slice(0, start) + REDACTED);
      blockEnd = i + 2;
    } else {
      result.push(segments[i] ?? "");
    }
  }
  return result.join(".");
}

// Where a JWT header starts in segments[i], or -1 when no window opens there.
function jwtHeaderStart(segments: string[], i: number): number {
  const payload = segments[i + 1] ?? "";
  if (i + 2 >= segments.length || payload.length < 2) {
    return -1;
  }
  const segment = segments[i] ?? "";
  // "eyJ" at a word boundary, as the regex's \b: segment start or after "-".
  let start = segment.startsWith(JWT_HEADER) ? 0 : -1;
  if (start === -1) {
    const afterDash = segment.indexOf(`-${JWT_HEADER}`);
    start = afterDash === -1 ? -1 : afterDash + 1;
  }
  // "eyJ" glued to a word character (jwt_eyJ..., %3DeyJ...) only when the
  // payload is base64url JSON too ('{"' or '{ '), so that words such as
  // "monkeyJump.example.com" are kept.
  if (payload.startsWith("ey")) {
    const glued = segment.indexOf(JWT_HEADER);
    if (glued !== -1 && (start === -1 || glued < start)) {
      start = glued;
    }
  }
  // The leftmost start leaves the longest header: if it is too short, all are.
  return start !== -1 && segment.length - start >= JWT_HEADER.length + 2
    ? start
    : -1;
}

// An absent/empty secret is itself a diagnostic, keep it.
function redactSecret(value: unknown) {
  return value === undefined || value === null || value === ""
    ? value
    : REDACTED;
}

function normalizeKey(key: string) {
  return key.toLowerCase().replace(/[-_]/g, "");
}

function isSdp(value: string) {
  return /^v=0\r?\n/.test(value) && /^m=/m.test(value);
}
